import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { WebSearchResult } from '../pages/api/search';
import { isSearchableQuery, resolveSearchState, SearchHttpError, shouldRetrySearch, type SearchState } from './search-state';

export const SEARCH_DEBOUNCE_MS = 250;

export type { SearchState };
export { isSearchableQuery, MIN_SEARCH_QUERY_LENGTH } from './search-state';

async function fetchSearch(query: string, signal?: AbortSignal): Promise<WebSearchResult[]> {
  const res = await apiFetch(`${API_BASE}/api/search?q=${encodeURIComponent(query)}`, {
    credentials: 'include',
    signal,
  });
  if (!res.ok) {
    throw new SearchHttpError(res.status);
  }
  const data = (await res.json()) as { results?: WebSearchResult[] };
  return data.results || [];
}

export function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useConversationSearch(query: string): SearchState {
  const trimmed = query.trim();
  const debounced = useDebounced(trimmed, SEARCH_DEBOUNCE_MS);

  const { data, error, fetchStatus } = useQuery({
    queryKey: queryKeys.search(debounced),
    queryFn: ({ signal }) => fetchSearch(debounced, signal),
    enabled: isSearchableQuery(debounced),
    // Re-typing a previous query returns its cached results instantly.
    staleTime: Infinity,
    // The app-wide default is no retry; a search retries a 5xx once (see `shouldRetrySearch`).
    retry: shouldRetrySearch,
  });

  return resolveSearchState({
    trimmed,
    debounced,
    cached: queryClient.getQueryData<WebSearchResult[]>(queryKeys.search(trimmed)),
    data,
    error,
    fetching: fetchStatus === 'fetching',
  });
}

export type { WebSearchResult };
